import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { useRefinePersona } from '@/hooks/use-personas';

interface PersonaRegenerateModalProps {
	personaId: string;
	label: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function PersonaRegenerateModal({ personaId, open, onOpenChange }: PersonaRegenerateModalProps) {
	const [feedbackText, setFeedbackText] = useState('');
	const refinePersona = useRefinePersona();

	const handleCancel = () => {
		onOpenChange(false);
		setFeedbackText('');
	};

	const handleRegenerate = async () => {
		if (!feedbackText.trim()) {
			toast.error('Tell us what went wrong first');
			return;
		}
		try {
			await refinePersona.mutateAsync({ id: personaId, feedbackText });
			onOpenChange(false);
			setFeedbackText('');
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Failed to start regeneration');
		}
	};

	return (
		<Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(next) : handleCancel())}>
			<DialogContent
				showCloseButton={false}
				className='max-w-[360px] gap-0 rounded-[20px] border-gray-500 bg-white/80 p-0 shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)] backdrop-blur-[6px]!'
				onClick={(e) => e.stopPropagation()}
			>
				<DialogHeader className='h-[50px] flex-row items-center justify-start px-3 py-2'>
					<DialogTitle className='text-sm font-semibold text-ink'>Regenerate Persona?</DialogTitle>
				</DialogHeader>
				<Separator />
				<div className='flex flex-col gap-1 p-4'>
					<label className='text-xs font-medium text-gray-900'>Tell us Whats wrong</label>
					<Textarea
						placeholder='Provide feedback...'
						value={feedbackText}
						onChange={(e) => setFeedbackText(e.target.value)}
						rows={4}
						className='min-h-[80px] resize-none rounded-2xl text-xs placeholder:text-gray-800'
					/>
					<p className='text-[11px] text-gray-800'>We will regenerate the persona based on this feedback</p>
				</div>
				<Separator />
				<DialogFooter className='p-3'>
					<Button variant='outline' className='rounded-full font-normal' onClick={handleCancel}>
						Cancel
					</Button>
					<Button className='rounded-full font-normal' onClick={handleRegenerate} disabled={refinePersona.isPending}>
						Regenerate
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
